import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api.js";

/** GET /api/v1/costs — running costs side by side (currencies differ, never summed):
 *  whatsapp = Meta per-message charges (WABA billing currency), ads = ad account
 *  currency, ai = MYR. */
export interface CostsSummary {
  range: { from: string; to: string };
  whatsapp: {
    /** false until the live Graph client is wired (no billing source on the fake). */
    available: boolean;
    /** The WABA's billing currency (ISO code, e.g. MYR) — costs are stated in it. */
    currency: string | null;
    total: number;
    volume: number;
    byCategory: Array<{
      category: string;
      volume: number;
      cost: number;
      /** billed-vs-free counts from per-message pricing receipts (status webhooks) */
      billed: number;
      free: number;
    }>;
    trend: Array<{ date: string; cost: number; volume: number }>;
  };
  ads: { total: number };
  ai: { total: number; replies: number };
}

/** Local YYYY-MM-DD, `back` days ago (0 = today). */
const iso = (back: number) => {
  const d = new Date();
  d.setDate(d.getDate() - back);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export function useCosts(businessId: string, days: number) {
  return useQuery<CostsSummary>({
    queryKey: [businessId, "costs", days],
    queryFn: () =>
      apiFetch(`/api/v1/costs?business=${businessId}&from=${iso(days - 1)}&to=${iso(0)}`),
    // Each fetch is a live Graph billing call (~2s) — don't re-hit Meta on every
    // mount/focus, and keep the previous numbers on screen while a range switch loads.
    staleTime: 120_000,
    placeholderData: (prev) => prev,
  });
}

export const CATEGORY_LABEL: Record<string, string> = {
  marketing: "Marketing",
  utility: "Utility",
  authentication: "Authentication",
  authentication_international: "Auth (international)",
  service: "Service (free)",
  referral_conversion: "Free entry point",
};

export const categoryLabel = (c: string) =>
  CATEGORY_LABEL[c] ?? c.charAt(0).toUpperCase() + c.slice(1);

const CURRENCY_SYMBOL: Record<string, string> = { MYR: "RM", USD: "$", SGD: "S$", EUR: "€" };

/** "RM 0.35" — symbol when we know the ISO code, bare number otherwise. */
export const money = (amount: number, currency: string | null) => {
  if (!currency) return amount.toFixed(2);
  return `${CURRENCY_SYMBOL[currency] ?? currency} ${amount.toFixed(2)}`;
};
